import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import HomePage from './components/home/HomePage';
import Prompt from './pages/Prompt';
import Gallery from './pages/Gallery';
import Plans from './pages/Plans';
import PresetGroupDetail from './pages/PresetGroupDetail';
import CreditsPage from './pages/CreditsPage';
import Success from './pages/Success';
import HistoryPage from './pages/HistoryPage';
import PhotoCatalog from './pages/PhotoCatalog';
import SearchCatalogPage from './pages/SearchCatalogPage';
import VideoCatalog from './pages/VideoCatalog';
import VideoGenerate from './pages/VideoGenerate';
import ProfilePage from './pages/ProfilePage';
import GalleryItemViewer from './pages/GalleryItemViewer';
import HomeRubricDetailPage from './pages/HomeRubricDetailPage';
import AppLayout from './components/AppLayout';
import BottomNav from './components/BottomNav';
import OfflineBanner from './components/OfflineBanner';
import InsufficientAccessSheet from './components/InsufficientAccessSheet';
import PersistentBalanceChip from './components/PersistentBalanceChip';
import { queryClient } from './core/queryClient';
import { useAuthStore } from './store/authStore';
import { useConsentStore } from './store/consentStore';
import ConsentGate from './components/legal/ConsentGate';
import { syncMarketingTechnologies } from './analytics/marketingConsent';
import Loading from './components/Loading';
import { hapticImpact, suppressHaptics } from './utils/haptics';
import {
  expand,
  getInitData,
  hideBackButton,
  initializeShell,
  isIOSPlatform,
  ready,
  setBackButton,
  setBackgroundColor,
  setBottomBarColor,
  setHeaderColor,
} from './platform';
import { trackClientEvent } from './analytics/track';
import useConsentGuard from './hooks/useConsentGuard';

export { getPersistentBalanceChipTopOffset } from './components/PersistentBalanceChip';

/**
 * GlobalOverlays — lives inside <Router/> so it can call useNavigate().
 * Owns the insufficient-access sheet state, fed by the axios response
 * interceptor (api/client.js → window.dispatchEvent('app:insufficient-access')).
 */
function GlobalOverlays() {
  const navigate = useNavigate();
  const { requireConsent } = useConsentGuard();
  const [insufficient, setInsufficient] = useState(null);

  useEffect(() => {
    const onIns = (e) => {
      trackClientEvent('paywall_shown', { trigger: 'no_credits' });
      setInsufficient({ message: e.detail?.message || null });
    };
    window.addEventListener('app:insufficient-access', onIns);
    return () => window.removeEventListener('app:insufficient-access', onIns);
  }, []);

  return (
    <InsufficientAccessSheet
      open={!!insufficient}
      message={insufficient?.message}
      onClose={() => setInsufficient(null)}
      onCta={() => {
        setInsufficient(null);
        requireConsent(
          () => navigate('/plans', {
            state: { paywallTrigger: 'no_credits', paywallAlreadyTracked: true },
          }),
          'purchase',
        );
      }}
    />
  );
}

function StandaloneBalanceChip() {
  const { pathname } = useLocation();
  const showStandaloneChip = pathname === '/profile';

  if (!showStandaloneChip) return null;

  return <PersistentBalanceChip />;
}

function ContentProtectionGuard() {
  useEffect(() => {
    let touchStart = null;
    let longPressTimer = null;
    let suppressNextClick = false;
    let suppressClickTimer = null;

    const editableSelector = [
      'input',
      'textarea',
      'select',
      '[contenteditable=""]',
      '[contenteditable="true"]',
    ].join(',');

    const isEditableTarget = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(editableSelector));
    };

    const prevent = (event) => {
      event.preventDefault();
    };

    const preventOutsideEditable = (event) => {
      if (!isEditableTarget(event.target)) {
        event.preventDefault();
      }
    };

    const clearLongPressTimer = () => {
      if (longPressTimer) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const clearSuppressClickTimer = () => {
      if (suppressClickTimer) {
        window.clearTimeout(suppressClickTimer);
        suppressClickTimer = null;
      }
    };

    const armClickSuppression = () => {
      suppressNextClick = true;
      suppressHaptics(1400);
      clearSuppressClickTimer();
      suppressClickTimer = window.setTimeout(() => {
        suppressNextClick = false;
        suppressClickTimer = null;
      }, 900);
    };

    const preventLongPress = (event) => {
      if (!touchStart || touchStart.cancelled || isEditableTarget(event.target)) return;
      event.preventDefault();
      armClickSuppression();
      window.getSelection?.()?.removeAllRanges?.();
    };

    const preventSuppressedClick = (event) => {
      if (!suppressNextClick) return;
      suppressNextClick = false;
      clearSuppressClickTimer();
      event.preventDefault();
      event.stopImmediatePropagation?.();
      event.stopPropagation();
    };

    const handleTouchStart = (event) => {
      clearLongPressTimer();
      if (isEditableTarget(event.target)) {
        touchStart = null;
        return;
      }

      const touch = event.touches?.[0];
      touchStart = touch
        ? { x: touch.clientX, y: touch.clientY, cancelled: false }
        : null;

      longPressTimer = window.setTimeout(() => {
        if (touchStart && !touchStart.cancelled) {
          armClickSuppression();
          window.getSelection?.()?.removeAllRanges?.();
        }
      }, 420);
    };

    const handleTouchMove = (event) => {
      if (!touchStart) return;
      const touch = event.touches?.[0];
      if (!touch) return;

      const dx = Math.abs(touch.clientX - touchStart.x);
      const dy = Math.abs(touch.clientY - touchStart.y);
      if (dx > 8 || dy > 8) {
        touchStart.cancelled = true;
        clearLongPressTimer();
      }
    };

    const handleTouchEnd = () => {
      touchStart = null;
      clearLongPressTimer();
    };

    document.addEventListener('contextmenu', prevent, true);
    document.addEventListener('click', preventSuppressedClick, true);
    document.addEventListener('copy', preventOutsideEditable);
    document.addEventListener('cut', preventOutsideEditable);
    document.addEventListener('selectstart', preventOutsideEditable);
    document.addEventListener('dragstart', prevent);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);
    document.addEventListener('touchforcechange', preventLongPress, { passive: false });

    return () => {
      clearLongPressTimer();
      clearSuppressClickTimer();
      document.removeEventListener('contextmenu', prevent, true);
      document.removeEventListener('click', preventSuppressedClick, true);
      document.removeEventListener('copy', preventOutsideEditable);
      document.removeEventListener('cut', preventOutsideEditable);
      document.removeEventListener('selectstart', preventOutsideEditable);
      document.removeEventListener('dragstart', prevent);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
      document.removeEventListener('touchforcechange', preventLongPress);
    };
  }, []);

  return null;
}

function TelegramBackButtonController() {
  const location = useLocation();
  const navigate = useNavigate();
  const backHandlerRef = useRef(null);

  const getBackButtonState = useCallback(() => {
    const pathname = window.location.pathname || '/';
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const createPopupOpen = pathname === '/' && params.get('create') === '1';
    const rootScreen = pathname === '/' && !createPopupOpen;
    const legalDocumentOpen = Boolean(
      document.querySelector('[data-testid="legal-document-viewer"]'),
    );

    return {
      pathname,
      params,
      createPopupOpen,
      legalDocumentOpen,
      shouldShow: legalDocumentOpen || !rootScreen,
    };
  }, []);

  useEffect(() => {
    backHandlerRef.current = () => {
      const legalDocumentOpen = document.querySelector(
        '[data-testid="legal-document-viewer"]',
      );
      if (legalDocumentOpen) {
        window.dispatchEvent(new Event('legal-document-viewer-close'));
        return;
      }

      const { pathname, params, createPopupOpen } = getBackButtonState();

      if (createPopupOpen) {
        params.delete('create');
        const nextSearch = params.toString();
        navigate(nextSearch ? `/?${nextSearch}` : '/', { replace: true });
        return;
      }

      if (pathname.startsWith('/video-generate/')) {
        navigate('/photo-catalog');
        return;
      }

      if (pathname === '/photo-catalog' || pathname === '/video-catalog') {
        navigate('/', { replace: true });
        return;
      }

      if (pathname.startsWith('/group/')) {
        if (window.history.length > 1) navigate(-1);
        else navigate('/photo-catalog', { replace: true });
        return;
      }

      if (/^\/gallery\/[^/]+/.test(pathname)) {
        navigate('/gallery');
        return;
      }

      if (pathname === '/gallery') {
        navigate('/', { replace: true });
        return;
      }

      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
    };
  }, [getBackButtonState, navigate]);

  const handleBack = useCallback(() => {
    hapticImpact('light');
    backHandlerRef.current?.();
  }, []);

  const syncTelegramBackButton = useCallback(() => {
    const { legalDocumentOpen, shouldShow } = getBackButtonState();
    setBackButton({
      visible: shouldShow,
      onClick: handleBack,
    });
    setHeaderColor(shouldShow || legalDocumentOpen ? '#010103' : '#FFDE59');
  }, [getBackButtonState, handleBack]);

  useLayoutEffect(() => {
    syncTelegramBackButton();
    const frame = requestAnimationFrame(syncTelegramBackButton);
    const timeout = window.setTimeout(syncTelegramBackButton, 80);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [location.pathname, location.search, syncTelegramBackButton]);

  useEffect(() => {
    const events = [
      'focus',
      'pageshow',
      'popstate',
      'hashchange',
      'legal-document-viewer-change',
    ];
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncTelegramBackButton();
    };

    events.forEach((eventName) => {
      window.addEventListener(eventName, syncTelegramBackButton);
    });
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(syncTelegramBackButton, 1000);

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, syncTelegramBackButton);
      });
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
      hideBackButton(handleBack);
    };
  }, [handleBack, syncTelegramBackButton]);

  return null;
}

function App() {
  const { initialize } = useAuthStore();
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const modalOpen = useConsentStore((state) => state.modalOpen);
  const marketingGranted = useConsentStore(
    (state) => Boolean(state.granted?.MARKETING),
  );
  const checkConsent = useConsentStore((state) => state.checkConsent);
  const markConsentRequired = useConsentStore((state) => state.requireConsent);
  const showConsentModal = useConsentStore((state) => state.showConsentModal);
  const dismissConsentModal = useConsentStore((state) => state.dismissConsentModal);

  useEffect(() => {
    let cleanupVisibility = null;

    const init = async () => {
      try {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (initializeShell({ fullscreen: isMobile })) {
          if (isIOSPlatform()) {
            document.documentElement.dataset.tgPlatform = 'ios';
          }

          // Handle app resume after background in a safe way for WebView.
          const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
              requestAnimationFrame(() => {
                try {
                  ready();
                  expand();
                } catch (e) {
                  if (import.meta.env.DEV) console.warn('Resume lifecycle call failed:', e);
                }
              });
            }
          };
          document.addEventListener('visibilitychange', onVisibilityChange);
          cleanupVisibility = () => document.removeEventListener('visibilitychange', onVisibilityChange);

          // Safe-top теперь идёт из native env(safe-area-inset-top) через
          // frontend/src/index.css (viewport-fit=cover в index.html).
          // Больше никакого hardcoded 80px — реальное устройство само
          // сообщит фактическую высоту notch/status-bar.
          setHeaderColor('#FFDE59');
          setBackgroundColor('#1a1a1a');
          setBottomBarColor('#1a1a1a');
        }

        // Always initialize the auth store — utils/api.js falls back to
        // VITE_DEV_TELEGRAM_ID when WebApp.initData is missing (local browser).
        // Without this, useAuthStore.user stays null and downstream pages
        // (e.g. Plans.jsx isDowngrade) treat the user as FREE.
        await initialize(getInitData());
        await checkConsent();
      } catch (error) {
        console.error('Init error:', error);
      }
    };
    init();

    return () => {
      if (cleanupVisibility) cleanupVisibility();
    };
  }, [checkConsent, initialize]);

  useEffect(() => {
    const onConsentRequired = (event) => {
      markConsentRequired(event.detail?.missing || []);
      showConsentModal(null, event.detail?.trigger || 'generate');
    };
    window.addEventListener('app:consent-required', onConsentRequired);
    return () => window.removeEventListener('app:consent-required', onConsentRequired);
  }, [markConsentRequired, showConsentModal]);

  useEffect(() => {
    syncMarketingTechnologies({
      consentRequired,
      marketingGranted,
    });
  }, [consentRequired, marketingGranted]);

  if (consentRequired === null) {
    return <Loading />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <>
        <Router>
          <ContentProtectionGuard />
          <OfflineBanner />
          <TelegramBackButtonController />
          <GlobalOverlays />
          <StandaloneBalanceChip />
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/search" element={<SearchCatalogPage />} />
              <Route path="/photo-catalog" element={<PhotoCatalog />} />
              <Route path="/photo-catalog/section/:sectionId" element={<HomeRubricDetailPage />} />
              <Route path="/video-catalog" element={<VideoCatalog />} />
              <Route path="/prompt" element={<Prompt />} />
              <Route path="/prompt/:presetId" element={<Prompt />} />
            </Route>

            {/* Экраны без bottom-nav — полноэкранные flow */}
            <Route path="/video-generate/:presetId" element={<VideoGenerate />} />
            <Route path="/group/:groupId" element={<PresetGroupDetail />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/gallery/:id" element={<GalleryItemViewer />} />
            <Route path="/success" element={<Success />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <BottomNav />
        </Router>
        <ConsentGate
          mode="modal"
          open={modalOpen}
          onDismiss={dismissConsentModal}
        />
      </>
    </QueryClientProvider>
  );
}

export default App;
